"use client"
import styled, { keyframes } from "styled-components"
import { motion } from "framer-motion"
import { useState, useEffect } from "react"

const glowPulse = keyframes`
  0%, 100% {
    box-shadow: 0 0 20px rgba(245, 158, 11, 0.3);
  }
  50% {
    box-shadow: 0 0 40px rgba(245, 158, 11, 0.6), 0 0 60px rgba(245, 158, 11, 0.4);
  }
`

const matrixRain = keyframes`
  0% {
    transform: translateY(-100%);
    opacity: 0;
  }
  10%, 90% {
    opacity: 1;
  }
  100% {
    transform: translateY(100vh);
    opacity: 0;
  }
`

const LoadingContainer = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: ${({ theme }) => theme.colors.background};
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: ${({ theme }) => theme.zIndex.modal};
  overflow: hidden;
`

const MatrixBackground = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  overflow: hidden;
  opacity: 0.1;
`

const MatrixColumn = styled.div`
  position: absolute;
  top: -100%;
  font-family: ${({ theme }) => theme.fonts.mono};
  font-size: 14px;
  color: ${({ theme }) => theme.colors.primary};
  animation: ${matrixRain} ${({ $duration }) => $duration}s linear infinite;
  animation-delay: ${({ $delay }) => $delay}s;
`

const LoadingContent = styled.div`
  text-align: center;
  z-index: 2;
`

const Logo = styled(motion.div)`
  width: 150px;
  height: 150px;
  margin: 0 auto 2rem;
  border-radius: 50%;
  background: ${({ theme }) => theme.colors.gradientPrimary};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: ${({ theme }) => theme.fontSizes["4xl"]};
  font-weight: ${({ theme }) => theme.fontWeights.bold};
  color: ${({ theme }) => theme.colors.background};
  animation: ${glowPulse} 2s ease-in-out infinite;
  position: relative;

  &::before {
    content: '';
    position: absolute;
    inset: -4px;
    border-radius: 50%;
    background: ${({ theme }) => theme.colors.gradientPrimary};
    z-index: -1;
    filter: blur(10px);
    opacity: 0.7;
  }
`

const LoadingTitle = styled(motion.h1)`
  font-size: ${({ theme }) => theme.fontSizes["4xl"]};
  font-weight: ${({ theme }) => theme.fontWeights.bold};
  color: ${({ theme }) => theme.colors.textPrimary};
  margin-bottom: 1rem;
  font-family: ${({ theme }) => theme.fonts.secondary};
  text-shadow: 0 0 20px rgba(245, 158, 11, 0.5);
`

const LoadingSubtitle = styled(motion.p)`
  font-size: ${({ theme }) => theme.fontSizes.xl};
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-bottom: 2rem;
  font-weight: ${({ theme }) => theme.fontWeights.medium};
`

const ProgressBar = styled(motion.div)`
  width: 300px;
  height: 4px;
  background: ${({ theme }) => theme.colors.backgroundTertiary};
  border-radius: 2px;
  margin: 2rem auto;
  overflow: hidden;
  position: relative;

  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    height: 100%;
    width: ${({ $progress }) => $progress}%;
    background: ${({ theme }) => theme.colors.gradientPrimary};
    border-radius: 2px;
    transition: width 0.3s ease;
    box-shadow: 0 0 10px rgba(245, 158, 11, 0.5);
  }
`

const LoadingText = styled(motion.div)`
  font-size: ${({ theme }) => theme.fontSizes.sm};
  color: ${({ theme }) => theme.colors.textMuted};
  font-family: ${({ theme }) => theme.fonts.mono};
  letter-spacing: 2px;
  text-transform: uppercase;
`

const LoadingScreen = () => {
  const [progress, setProgress] = useState(0)
  const [loadingText, setLoadingText] = useState("Initializing...")

  const loadingSteps = ["Initializing...", "Loading Assets...", "Preparing Interface...", "Almost Ready...", "Welcome!"]

  useEffect(() => {
    const interval = setInterval(() => {
      setProgress((prev) => {
        const newProgress = prev + 2
        const stepIndex = Math.floor(newProgress / 20)
        if (stepIndex < loadingSteps.length) {
          setLoadingText(loadingSteps[stepIndex])
        }
        return newProgress > 100 ? 100 : newProgress
      })
    }, 60)

    return () => clearInterval(interval)
  }, [])

  // Generate matrix columns
  const matrixColumns = Array.from({ length: 50 }, (_, i) => {
    const chars = "01アイウエオカキクケコサシスセソタチツテトナニヌネノハヒフヘホマミムメモヤユヨラリルレロワヲン"
    const columnText = Array.from({ length: 20 }, () => chars[Math.floor(Math.random() * chars.length)]).join("")

    return (
      <MatrixColumn
        key={i}
        style={{ left: `${(i / 50) * 100}%` }}
        $duration={Math.random() * 3 + 2}
        $delay={Math.random() * 2}
      >
        {columnText}
      </MatrixColumn>
    )
  })

  return (
    <LoadingContainer>
      <MatrixBackground>{matrixColumns}</MatrixBackground>

      <LoadingContent>
        <Logo
          initial={{ scale: 0, rotate: -180 }}
          animate={{ scale: 1, rotate: 0 }}
          transition={{ duration: 1, type: "spring", stiffness: 100 }}
        >
          AN
        </Logo>

        <LoadingTitle
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5, duration: 0.8 }}
        >
          Aline NIYONIZERA
        </LoadingTitle>

        <LoadingSubtitle initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.8, duration: 0.8 }}>
          Aline NIYONIZERA's Portfolio 
        </LoadingSubtitle>

        <ProgressBar
          $progress={progress}
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 1, duration: 0.5 }}
        />

        <LoadingText initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.2, duration: 0.5 }}>
          {loadingText}
        </LoadingText>
      </LoadingContent>
    </LoadingContainer>
  )
}

export default LoadingScreen
